import * as React from "react"
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { Typography, IconButton, Box, Divider } from "@mui/material"
import { DragIndicator as GripVertical, Close as X } from "@mui/icons-material"
import DraggableContent, { useDragHandle } from "./DraggableResizableContent"

function DialogHeaderWithDrag({ title, onOpenChange }) {
    const { attributes, listeners } = useDragHandle();
    return (
        <Box
            {...attributes}
            {...listeners}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: 2,
                py: 1.5,
                cursor: 'move',
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.2s',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
            }}
        >
            <GripVertical sx={{ fontSize: 18, opacity: 0.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                {title}
            </Typography>
            <IconButton
                size="small"
                onClick={() => onOpenChange(false)}
                sx={{
                    opacity: 0.7,
                    '&:hover': { opacity: 1, color: 'error.main' },
                    transition: 'opacity 0.2s'
                }}
            >
                <X sx={{ fontSize: 18 }} />
            </IconButton>
        </Box>
    )
}

export function DraggableDialog({
    open,
    onOpenChange,
    title,
    children,
    id,
    width,
    height,
    initialPosition
}) {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        })
    )

    const [position, setPosition] = React.useState({ x: 300, y: 300 });

    React.useEffect(() => {
        if (open) {
            if (initialPosition) {
                setPosition(initialPosition);
            } else {
                let w = 600;
                if (typeof width === 'string' && (width.includes('vw') || width.includes('%'))) {
                    w = (parseFloat(width) / 100) * window.innerWidth;
                } else {
                    w = parseInt(width) || 600;
                }

                let h = 400;
                if (typeof height === 'string' && (height.includes('vh') || height.includes('%'))) {
                    h = (parseFloat(height) / 100) * window.innerHeight;
                } else {
                    h = parseInt(height) || 400;
                }

                setPosition({
                    x: Math.max(0, (window.innerWidth - w) / 2),
                    y: Math.max(0, (window.innerHeight - h) / 2)
                });
            }
        }
    }, [open, width, height, initialPosition]);
    function handleDragEnd(event) {
        const { delta } = event

        setPosition((prev) => ({
            x: prev.x + delta.x,
            y: prev.y + delta.y,
        }))
    }

    if (!open) return null;

    return (
        <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
            <DraggableContent id={id} position={position} open={open} width={width} height={height}>
                <DialogHeaderWithDrag onOpenChange={onOpenChange} title={title} />
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {children}
                </Box>
            </DraggableContent>
        </DndContext>
    )
}
